import { createSelector } from '@reduxjs/toolkit';
import { Map as ImmutableMap, Set as ImmutableSet, List, OrderedMap } from 'immutable';
import { LruMapCache, LruObjectCache, createCachedSelector } from 're-reselect';

import store from '../../utils/redux-store';
import { FEATURE_FLAGS_REDUCER_KEY, featureFlagsSelectors } from '../feature-flags';

import { ACCOUNTS_REDUCER_KEY, Account, AccountProps, AccountSubType, AccountType } from './accountsTypes';
import { hasCheckNumberField } from './accountsUtils';

import type RootState from 'app-components/utils/redux-store/rootState';

import { getId } from '../core/resourceStoreTypes';
import * as utils from './accountsUtils';

type AccountSubTypeOrder = Partial<Record<`${AccountType}:${AccountSubType | ''}`, number>>;

const spendingAccountsOrder: Record<string, number> = {
  'CREDIT:CREDIT_CARD': 0,
  'BANK:CHECKING': 1,
  'CASH:': 2,
  'CREDIT:LINE_OF_CREDIT': 3,
  'CREDIT:OTHER_CREDIT': 4,
  'BANK:OTHER_BANK': 5,
} satisfies AccountSubTypeOrder;

const investmentsOrder: Record<string, number> = {
  INVESTMENT: 0,
};

const accountGroupNameMap: Partial<Record<AccountSubType | AccountType | `Q${AccountSubType}`, string>> = {
  BROKERAGE: 'Brokerage',
  OTHER_INVESTMENTS: 'Other',
  IRA: 'Retirement',
  ROTH_401K: 'Retirement',
  ROTH_IRA: 'Retirement',
  SIMPLE_IRA: 'Retirement',
  UGMA_UTMA: 'Other',
  529: 'Other',
  Q401K: 'Retirement',
  Q529: 'Other',
  KEOGH: 'Retirement',
  Q403B: 'Other',
  SEP_IRA: 'Retirement',
  '401K': 'Retirement',
  '403B': 'Retirement',
};

/*
 * For these selectors, state is global state as it is provided to React (not specific to accounts)
 * We use them to select into the store and get data for this REDUCER_KEY
 */
export const getAccountStore = (state: Pick<RootState, ACCOUNTS_REDUCER_KEY>) => state[ACCOUNTS_REDUCER_KEY];

export const getLoadPending = createSelector([getAccountStore], (accountStore) => accountStore.loadPending);
export const getIsLoading = createSelector([getAccountStore], (accountStore) => accountStore.isLoading);
export const getError = createSelector([getAccountStore], (accountStore) => accountStore.error);
export const getResourcesById = createSelector([getAccountStore], (accountStore) => accountStore.resourcesById);
export const getAccountsByIdIncludingGoals = getResourcesById;

export const getLastSyncDate = createSelector([getAccountStore], (accountStore) => accountStore.lastSyncDate);
export const getAccountById = (state: Pick<RootState, ACCOUNTS_REDUCER_KEY> = store!.getState(), id?: string | null) =>
  id ? getAccountStore(state).resourcesById.get(id) : undefined;

// updated to not include Goal accounts
export const getAccountsById = createSelector([getResourcesById], (accountsById) =>
  accountsById.filter((acct) => acct.type !== 'GOAL'),
);

export const getOpenAccountsById = createSelector(getAccountsById, (accountsById) =>
  accountsById.filter((acct) => !acct.isClosed),
);

export const getOpenAccountIds = createSelector(getOpenAccountsById, (openAccounts) =>
  ImmutableSet(openAccounts.keys()),
);

export const getGoalAccountsById = createSelector([getResourcesById], (accountsById) =>
  accountsById.filter((acct) => acct.type === 'GOAL'),
);

export const getAccountIdsForInvestments = createSelector([getAccountsById], (accountsById) => {
  const accounts = accountsById.filter((acct) => acct.type === 'INVESTMENT');
  return {
    groupedAccounts: accounts
      .toList()
      .groupBy((x) => (x.subType ? accountGroupNameMap[x.subType] || 'Other' : accountGroupNameMap[x.type] || 'Other'))
      .sortBy((item, key) => investmentsOrder[key] || 100),
    accountIds: List([...accounts.keys()]),
  };
});

export const isAssetLiabilityAccount = (account: AccountProps) =>
  account?.type === 'LOAN' ||
  account?.type === 'OTHER_ASSET' ||
  account?.type === 'OTHER_LIABILITY' ||
  account?.type === 'REAL_ESTATE' ||
  account?.type === 'VEHICLE';

export const isCashAccount = (account?: AccountProps) =>
  account?.type === 'CREDIT' || account?.type === 'BANK' || account?.type === 'CASH';

export const isGoalAccount = (account?: AccountProps) => account?.type === 'GOAL';

export const isBankAccount = (account?: AccountProps) =>
  (account?.type === 'BANK' || account?.subType === 'SAVINGS') && !account?.isClosed;

export const isInvestmentAccount = (account?: AccountProps) => account?.type === 'INVESTMENT';

export const isLoanAccount = (account?: AccountProps) => account?.type === 'LOAN';

export const isConnectedLoanAccount = (account?: AccountProps) =>
  account?.type === 'LOAN' && utils.isAccountConnected(account);

export const getLoanAccountIds = createSelector(getAccountsById, (accountsById) => {
  const accounts = accountsById.filter((acct) => isLoanAccount(acct));
  return ImmutableSet([...accounts.keys()]);
});

export const getConnectedLoanAccountIds = createSelector(getAccountsById, (accountsById) =>
  ImmutableSet(accountsById.filter(isConnectedLoanAccount).keys()),
);

export const gotNonLoanAccountIds = createSelector(getAccountsById, (accountsById) =>
  ImmutableSet(accountsById.filter((acct) => !isLoanAccount(acct)).keys()),
);

export const getExcludedBudgetAccounts = createSelector(getAccountsById, (accountsById) =>
  accountsById.filter((acct) => acct.isExcludedFromBudgets),
);
export const getReportableAccountsById = createSelector(getAccountsById, (accountsById) =>
  accountsById.filter((account) => !account.isExcludedFromReports),
);

export const getHasReportableAccounts = createSelector(
  getReportableAccountsById,
  (accountsById) => accountsById.size > 0,
);

export const getVisibleAccountsById = createSelector(getAccountsById, (accountsById) =>
  accountsById.filter((x) => !x.isExcludedFromAccountBar),
);

const emptyAccountMap = ImmutableMap<string, Account>();
export const getCashAccounts = createSelector(getAccountsById, (accountsById) =>
  accountsById == null ? emptyAccountMap : accountsById.filter((account) => isCashAccount(account)),
);

export const getBankAccounts = createSelector(getAccountsById, (accountsById) =>
  accountsById == null ? emptyAccountMap : accountsById.filter((account) => isBankAccount(account)),
);

export const getRealEstateAccounts = createSelector(getAccountsById, (accountsById) =>
  accountsById == null ? emptyAccountMap : accountsById.filter((account) => account.type === 'REAL_ESTATE'),
);

export const getCashFlowTransactionOnlyAccounts = createSelector(getAccountsById, (accountsById) =>
  accountsById?.filter((account) => isCashAccount(account) || isAssetLiabilityAccount(account)),
);

export const getStripeAccounts = createSelector(getAccountsById, (accountsById) =>
  accountsById == null ? emptyAccountMap : accountsById.filter((account) => utils.isStripeAccount(account)),
);

export const getOpenOrSelectedCashAccounts = createSelector(
  [getCashFlowTransactionOnlyAccounts, (_state: unknown, selectedAccountId?: string) => selectedAccountId],
  (accountsById, selectedAccountId) =>
    accountsById == null
      ? emptyAccountMap
      : accountsById.filter((account) => !account?.isClosed || selectedAccountId === account?.id),
);

export const getInvestmentAccounts = createSelector(getAccountsById, (accountsById) =>
  accountsById == null ? emptyAccountMap : accountsById.filter((x) => isInvestmentAccount(x)),
);

export const getOpenOrSelectedInvestmentAccounts = createSelector(
  [getInvestmentAccounts, (_state: unknown, selectedAccountId: string) => selectedAccountId],
  (accountsById, selectedAccountId) =>
    accountsById == null
      ? emptyAccountMap
      : accountsById.filter((account) => !account?.isClosed || selectedAccountId === account?.id),
);

export const getNonConnectedInvestmentAccounts = createSelector(getInvestmentAccounts, (accountsById) =>
  accountsById?.filter((account) => utils.isAccountManual(account)),
);

//-------------------------------------------------------------------------------------------------
// Selector that returns a the set of all account IDs. The returned set will only change when an
// account is added/deleted.
//-------------------------------------------------------------------------------------------------
export const getAllAccountIds = createSelector(
  getAccountsByIdIncludingGoals,
  (() => {
    // eslint-disable-line func-names
    let allAccountIds = ImmutableSet<string>();
    return (accountsById: ImmutableMap<string, Account>) => {
      const addedAccountsById = accountsById.filter((_, id) => !allAccountIds.has(id));
      const removedAccountIds = allAccountIds.filter((id) => !accountsById.has(id));
      if (!addedAccountsById.size && !removedAccountIds.size) {
        return allAccountIds;
      }
      allAccountIds = allAccountIds.withMutations((set) => {
        addedAccountsById.forEach((_, id) => set.add(id));
        removedAccountIds.forEach((id) => set.add(id));
      });
      return allAccountIds;
    };
  })(),
);

//-------------------------------------------------------------------------------------------------
// Selector that returns a map of account types keyed by the account ID. The returned value will
// only change when accounts are added/deleted or when an account type has changed.
//-------------------------------------------------------------------------------------------------
export const getAccountTypesById = createSelector(
  getAccountsByIdIncludingGoals,
  (() => {
    // eslint-disable-line func-names
    let accountTypesById = ImmutableMap<string, AccountType>();
    return (accountsById: ImmutableMap<string, Account>) => {
      const removedAccountTypesById = accountTypesById.filter((_, id) => !accountsById.has(id));
      accountTypesById = accountTypesById.withMutations((map) => {
        removedAccountTypesById.forEach((_, id) => map.remove(id));
        accountsById.forEach((account, accountId) => map.set(accountId, account.type));
      });
      return accountTypesById;
    };
  })(),
);

//-------------------------------------------------------------------------------------------------
// Selector that returns a the set of goal account IDs. This can be used to quickly determine whether
// an account is a GOAL account or not. The returned set will only change when a goal account is
// added/deleted and an existing account has changed to/from a GOAL account.
//-------------------------------------------------------------------------------------------------
export const getGoalAccountIds = createSelector(
  getAccountTypesById,
  (function cobinerClosure() {
    // if ur are lame use this ==> eslint-disable-line func-names  :-)
    let goalAccountIds = ImmutableSet<string>();
    return (accountTypesById: ImmutableMap<string, AccountType>) => {
      const removedGoalAccountIds = goalAccountIds.filter((id) => !accountTypesById.has(id));
      goalAccountIds = goalAccountIds.withMutations((set) => {
        removedGoalAccountIds.forEach((id) => set.remove(id));
        accountTypesById.forEach((type, id) => {
          if (type === 'GOAL') {
            set.add(id);
          } else {
            set.remove(id);
          }
        });
      });
      return goalAccountIds;
    };
  })(),
);

export const getAccountsByInstitutionLoginId = createSelector(getAccountsById, (accountsById) =>
  accountsById?.reduce((accountsByInstitutionLoginIds, account) => {
    const institutionLoginId = account.institutionLoginId || '0';
    const accounts = accountsByInstitutionLoginIds.get(institutionLoginId) || List<Account>();
    return accountsByInstitutionLoginIds.set(institutionLoginId, accounts.push(account));
  }, ImmutableMap<string, List<Account>>()),
);

export const getAccountsForInstitutionLoginId = createSelector(
  [
    getAccountsByInstitutionLoginId,
    (_state: unknown, props: { institutionLoginId: string }) => props.institutionLoginId,
  ],
  (accountsByInstitutionId, institutionLoginId) => accountsByInstitutionId.get(institutionLoginId) || List<Account>(),
);

export const getAccountIdsForInstitutionLoginId = createSelector(getAccountsForInstitutionLoginId, (accounts) =>
  accounts.map(getId),
);

// This selector returns all non-connect accounts which supports an FI connectable account type.
export const getFiConnectableAccounts = createSelector(getAccountsById, (accountsById) =>
  accountsById
    .valueSeq()
    .filter((account) => !account.isConnected && utils.isAccountFiConnectable(account))
    .toList(),
);

export const getSpendingAccounts = createSelector(getVisibleAccountsById, (accounts) =>
  OrderedMap(
    accounts.filter((account) =>
      Object.prototype.hasOwnProperty.call(spendingAccountsOrder, `${account.type}:${account.subType}`),
    ),
  ).sort(
    (account1, account2) =>
      spendingAccountsOrder[`${account1.type}:${account1.subType}`] -
      spendingAccountsOrder[`${account2.type}:${account2.subType}`],
  ),
);

const incomeAccountsOrder: Record<string, number> = {
  'BANK:CHECKING': 0,
  'BANK:OTHER_BANK': 1,
  'CASH:': 2,
  'BANK:SAVINGS': 3,
  'BANK:CD': 4,
  'BANK:MONEY_MARKET': 5,
} satisfies AccountSubTypeOrder;
export const getIncomeAccounts = createSelector(getVisibleAccountsById, (accounts) =>
  OrderedMap(
    accounts.filter((account) =>
      Object.prototype.hasOwnProperty.call(incomeAccountsOrder, `${account.type}:${account.subType}`),
    ),
  ).sort(
    (account1, account2) =>
      incomeAccountsOrder[`${account1.type}:${account1.subType}`] -
      incomeAccountsOrder[`${account2.type}:${account2.subType}`],
  ),
);

const billAccountsOrder: Record<string, number> = {
  'BANK:CHECKING': 0,
  'CREDIT:CREDIT_CARD': 1,
  'CASH:': 2,
  'BANK:OTHER_BANK': 3,
  'CREDIT:OTHER_CREDIT': 4,
  'CREDIT:LINE_OF_CREDIT': 5,
  'BANK:MONEY_MARKET': 6,
} satisfies AccountSubTypeOrder;
export const getBillAccounts = createSelector(getVisibleAccountsById, (accounts) =>
  OrderedMap(
    accounts.filter((account) =>
      Object.prototype.hasOwnProperty.call(billAccountsOrder, `${account.type}:${account.subType}`),
    ),
  ).sort(
    (account1, account2) =>
      billAccountsOrder[`${account1.type}:${account1.subType}`] -
      billAccountsOrder[`${account2.type}:${account2.subType}`],
  ),
);

const savingAccountsOrder: Record<string, number> = {
  'BANK:SAVINGS': 0,
  'BANK:CD': 1,
  'BANK:MONEY_MARKET': 2,
} satisfies AccountSubTypeOrder;
export const getSavingAccounts = createSelector(getVisibleAccountsById, (accounts) =>
  OrderedMap(
    accounts.filter((account) =>
      Object.prototype.hasOwnProperty.call(savingAccountsOrder, `${account.type}:${account.subType}`),
    ),
  ).sort(
    (account1, account2) =>
      savingAccountsOrder[`${account1.type}:${account1.subType}`] -
      savingAccountsOrder[`${account2.type}:${account2.subType}`],
  ),
);

export const accountIds = createCachedSelector(
  (state: Pick<RootState, ACCOUNTS_REDUCER_KEY>, accounts: ImmutableMap<string, Account> | undefined) =>
    accounts || getAccountsById(state),
  (accounts) => accounts.filter((account) => account.id).map((account) => account.id),
)({
  keySelector: (state, accounts) => accounts?.hashCode() ?? 'no-accounts',
  cacheObject: new LruObjectCache({ cacheSize: 8 }),
});

export const getTransactionAccounts = createSelector(
  [
    getAccountsById, // goals are excluded by default
    (state: Pick<RootState, typeof FEATURE_FLAGS_REDUCER_KEY>) =>
      featureFlagsSelectors.getFeatureFlag(state, 'investmentTransactions'),
  ],
  (accountsById, investmentTransactions) =>
    accountsById.filter(
      (account) =>
        (account?.type !== 'INVESTMENT' || investmentTransactions) &&
        (account?.type !== 'LOAN' || !utils.isAccountConnected(account)),
    ),
);

// TODO: this logic is the same for above getTransactionAccounts, but kept for compatibility
// Right now they are the same, but may not always be the same, so we're keeping this for
// if allowed scheduledTransaction accounts changes
export const getScheduledTransactionAccounts = createSelector(getAccountsById, (accountsById) =>
  accountsById.filter(
    (account) => account?.type !== 'INVESTMENT' && (account?.type !== 'LOAN' || !utils.isAccountConnected(account)),
  ),
);

export const getAccountsByIds = createCachedSelector(
  [getAccountsById, (state: unknown, ids: List<string> | undefined) => ids],
  (accountsById, ids) => accountsById && accountsById.filter((account) => account.id && ids?.includes(account.id)),
)({
  keySelector: (state, ids) => ids?.hashCode() ?? 'no-accounts',
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

export const getOpenAccountsByIds = createCachedSelector(
  [getOpenAccountsById, (state: unknown, ids: List<string> | undefined) => ids],
  (accountsById, ids) => accountsById && accountsById.filter((account) => account.id && ids?.includes(account.id)),
)({
  keySelector: (state, ids) => ids?.hashCode() ?? 'no-accounts',
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

export const getPendingTxnExcludedAccountIds = createSelector(getAccountsById, (accountsById) =>
  accountsById.filter((account) => account.isBankPendingTxnsExcluded).map((account) => account.id),
);

export const getRefundAccounts = createSelector(getCashAccounts, (accountsById) =>
  accountsById?.filter((account) => !account.isClosed),
);

export const removeClosedAccountsFromIds = createSelector(
  [getOpenAccountIds, (state: unknown, providedAccountIds: List<string>) => providedAccountIds],
  (openAccountIds, providedAccountIds) => providedAccountIds?.filter?.((id) => openAccountIds.has(id)),
);

//-------------------------------------------------------------------------------------------------
// Business Related Selectors
//-------------------------------------------------------------------------------------------------

// deprecated - this assumed there is only one A/R account
export const getAccountsReceivable = createSelector(getAccountsById, (accountsById) =>
  accountsById.find((account) => account.type === 'ACCOUNTS_RECEIVABLE'),
);

export const getAllArAccounts = createSelector(getAccountsById, (accountsById) =>
  accountsById.filter((account: AccountProps) => account.type === 'ACCOUNTS_RECEIVABLE').toList(),
);

export const getAllArAccountIds = createSelector(getAllArAccounts, (accounts) => accounts.map((account) => account.id));

export const getArAccountByBusiness = createSelector(getAccountsById, (accountsById) =>
  accountsById
    .filter((account: AccountProps) => account.type === 'ACCOUNTS_RECEIVABLE')
    .groupBy((account) => account.businessId)
    .map((accounts) => accounts.first()),
);

export const getArAccountForBusinessId = createSelector(
  getArAccountByBusiness,
  (_state, businessId: string) => businessId,
  (accountsByBusiness, businessId) => accountsByBusiness.get(businessId),
);

export const getPaymentSrcsByAccountId = createSelector(getOpenAccountsById, (accountsById) =>
  OrderedMap(accountsById.filter((account) => utils.doesAccountSupportPaymentSrcs(account))).sort((a, b) =>
    (a.name || '').localeCompare(b.name || ''),
  ),
);

// Refund compatible accounts are generally asset accounts; however, some types or subtypes
// are not applicable.
//
export const getRefundSrcsByAccountId = createSelector([getOpenAccountsById], (accountsById) =>
  OrderedMap(
    accountsById
      .filter((account) => utils.doesAccountSupportRefundSrcs(account))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
  ),
);

const getPriorityForDefaultPaymentAccount = (account: AccountProps) => {
  if (account.type === 'OTHER_ASSET' && account.subType === 'UNDEPOSITED_FUNDS') {
    return 6;
  }
  if (account.usageType === 'BUSINESS' && account.type !== 'PAYMENT') {
    return 5;
  }
  if (account.type === 'BANK') {
    if (account.subType === 'CHECKING') {
      return 4;
    }
    return 3;
  }
  if (account.type === 'CASH') {
    return 2;
  }
  if (account.type === 'OTHER_ASSET') return 1;
  return 0;
};

// This should return the undeposited funds account. If more than one, then return first one found.
// If there is no undeposited funds account, will try to pick best one based on usage type, type
// and subtype.
//
// Take business into account when selecting the preferred account. The business could be
//       passed into the selector as a parameter and if not passed in, we could use the default
//       business.
// FIXME: We can’t retrieve the default business due to a circular dependency—>businessSelectors rely on accountSelectors.
export const getDefaultPaymentSrcAccount = createSelector(
  [getPaymentSrcsByAccountId, (_state: unknown, businessId?: string) => businessId],
  (accountsById, businessId) => {
    let defaultPaymentSrcAccount: undefined | Account;
    let defaultPaymentSrcAccountPriority = 0;
    const businessAccountsById = businessId
      ? accountsById.filter((account) => account.businessId === businessId)
      : accountsById;
    businessAccountsById.forEach((account) => {
      const priority = getPriorityForDefaultPaymentAccount(account);
      if (priority > defaultPaymentSrcAccountPriority) {
        defaultPaymentSrcAccount = account;
        defaultPaymentSrcAccountPriority = priority;
      }
    });
    return defaultPaymentSrcAccount;
  },
);

export const getDefaultRefundSrcAccount = createSelector(
  [getAccountsById, (_state: unknown, businessId: string) => businessId],
  (accountsById, businessId) => {
    return accountsById?.find(
      (account) => account.businessId === businessId && account.type === 'BANK' && account.subType === 'CHECKING',
    );
  },
);

export const shouldAccountsDisplayCheckNumberField = createSelector(
  getAccountsByIds,
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'checkNumberTxnDetails'),
  (accounts, isFfEnabled) => {
    if (!isFfEnabled) {
      return false;
    }

    return Boolean(accounts.find(hasCheckNumberField));
  },
);

export const shouldAccountDisplayCheckNumberField = createSelector(
  getAccountById,
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'checkNumberTxnDetails'),
  (account, isFfEnabled) => {
    if (!isFfEnabled) {
      return false;
    }

    return hasCheckNumberField(account);
  },
);

export const stripeAccountForBizId = createSelector(
  [getResourcesById, (_s, businessId?: string | null) => businessId],
  (accounts, businessId) =>
    businessId
      ? accounts?.find((acc) => utils.isStripeAccount(acc) && acc.type === 'PAYMENT' && acc.businessId === businessId)
      : undefined,
);

export const stripeAccountPaymentMethods = createSelector(
  stripeAccountForBizId,
  (account) => account?.aggregators?.get(0)?.paymentMethods,
);

export const getSalesTaxAccountsByBusiness = createSelector(getAccountsById, (accountsById) =>
  accountsById
    .filter((account: AccountProps) => account.type === 'OTHER_LIABILITY' && account.subType === 'SALES_TAX')
    .groupBy((account) => account.businessId)
    .map((accounts) => accounts.first()),
);

export const getQBPAccountIds = createSelector(getAccountsById, (accounts) => {
  return accounts
    ?.toList()
    ?.filter(utils.isQBPAccount)
    ?.map((account) => account.id);
});
